import { Fragment } from 'react';

import { ChevronsRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { cn } from '@/lib/utils';
import { AuthStepStatus, SynthesisResponseCode } from '@/shared/types/tryIt';

import { hasTurnIssue } from './buildTurnIssues';
import {
  selectIsReplay,
  selectSelectedTurnIndex,
  selectSelectedTurnRequestId,
} from '../../../redux/selectors';
import { TurnJumpLabel, TurnSourceLabel, useTurnSource } from '../../../shared/TurnSource';
import { ConversationTurn } from '../../../types/conversation';
import { trailJumps, turnJumps } from '../../../utils/turnJumps';

type TurnStatus =
  'authenticating' | 'handover' | 'no_response' | 'answered_with_issue' | 'answered';

const turnStatusDisplay: Record<TurnStatus, { labelKey: string; className: string }> = {
  authenticating: { labelKey: 'try_it.auth.status_in_progress', className: 'text-warning' },
  handover: { labelKey: 'actions.types.handover', className: 'text-warning' },
  no_response: { labelKey: 'try_it.no_response', className: 'text-destructive' },
  answered_with_issue: { labelKey: 'try_it.answered', className: 'text-warning' },
  answered: { labelKey: 'try_it.answered', className: 'text-text-success-firm' },
};

const getTurnStatus = (turn: ConversationTurn): TurnStatus => {
  const isAuthenticating =
    turn.metadata.isAuthentication &&
    turn.metadata.authentication?.status !== AuthStepStatus.COMPLETED;
  if (isAuthenticating) {
    return 'authenticating';
  }
  if (turn.metadata.collection?.response_code === SynthesisResponseCode.FORCE_HANDOVER) {
    return 'handover';
  }
  if (turn.responses.length === 0) {
    return 'no_response';
  }
  if (hasTurnIssue(turn)) {
    return 'answered_with_issue';
  }
  return 'answered';
};

type Props = { turn: ConversationTurn };

export const TurnHeader = ({ turn }: Props) => {
  const { t } = useTranslation();
  const turnIndex = useSelector(selectSelectedTurnIndex);
  const requestId = useSelector(selectSelectedTurnRequestId);

  const isReplay = useSelector(selectIsReplay);

  const source = useTurnSource(turn);
  const jumps = isReplay ? [] : trailJumps(source?.name, turnJumps(turn));
  const hasStatus = !turn.pending;
  const { labelKey, className } = turnStatusDisplay[getTurnStatus(turn)];

  return (
    <div className="flex flex-col items-start gap-2 self-stretch">
      <div className="flex items-center gap-2">
        <span className="text-sm leading-5 font-semibold text-foreground">
          {t('try_it.turn_number', { 0: (turnIndex ?? 0) + 1 })}
        </span>
        {requestId && (
          <CopyButton
            className="flex items-center justify-center"
            data={requestId}
            tooltip={t('try_it.copy_request_id')}
            tooltipSide="right"
            buttonSize="icon-sm"
            iconSize={16}
          />
        )}
      </div>

      {(source || hasStatus) && (
        <div className="flex flex-wrap items-center gap-2">
          {source && <TurnSourceLabel source={source} />}
          {jumps.map((jump, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`${jump.nodeId}-${index}`}>
              {(source || index > 0) && (
                <ChevronsRightIcon size={16} className="shrink-0 text-foreground-muted" />
              )}
              <TurnJumpLabel nodeId={jump.nodeId} name={jump.name} />
            </Fragment>
          ))}
          {(source || jumps.length > 0) && hasStatus && (
            <span className="text-sm leading-5 text-foreground-muted">·</span>
          )}
          {hasStatus && (
            <span className={cn('text-sm leading-none font-medium', className)}>{t(labelKey)}</span>
          )}
        </div>
      )}
    </div>
  );
};
